import styled from 'styled-components';
import { isColorDark } from './TeamOrgStructureUtils';

export const OuterWrapper = styled.div`
  position: relative;
  width: 100%;
  min-height: 0;
  height: calc(100vh - 260px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const Container = styled.div`
  position: relative;
  width: 100%;
  min-height: 0;
  flex: 1 1 auto;
  overflow-x: auto; /* allow horizontal scrolling when chart is wide */
  overflow-y: auto;
  padding: 16px;
  cursor: auto;
`;

export const Chart = styled.div<{ $layoutMode?: 'horizontal' | 'tree' }>`
  display: flex;
  justify-content: ${props => (props.$layoutMode === 'tree' ? 'flex-start' : 'center')};
  gap: 40px;
  align-items: flex-start;
  padding: 20px;
  min-width: max-content; /* prevents columns from wrapping/shrinking so horizontal scroll appears */
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

export const ChartHeader = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
  z-index: 20;
  background: #ffffff;
  padding: 8px 12px;
  border-radius: 12px;
  margin-bottom: 16px;
`;

export const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const FilterSelect = styled.select`
  min-width: 200px;
  height: 38px;
  padding: 0 36px 0 12px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #ffffff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23374151' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat right 12px center;
  background-size: 10px 6px;
  color: #111827;
  font-size: 13px;
  line-height: 1.2;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  &:focus-visible,
  &:focus {
    outline: none;
    border-color: #94a3b8;
  }
`;

export const ScrollControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
`;

export const ScrollButton = styled.button`
  appearance: none;
  width: 38px;
  height: 38px;
  min-width: 38px;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  color: #334155;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: background-color 120ms ease, border-color 120ms ease, transform 120ms ease, opacity 120ms ease;

  &:hover:not(:disabled) {
    background: #eef2ff;
    border-color: #94a3b8;
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }
`;

export const LayoutToggleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const LayoutToggleButton = styled.button<{ active?: boolean }>`
  appearance: none;
  border: 1px solid #cbd5e1;
  background: ${props => (props.active ? '#ffffff' : '#f8fafc')};
  color: #334155;
  border-radius: 10px;
  width: 38px;
  height: 38px;
  min-width: 38px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease, transform 120ms ease;

  &:hover {
    background: ${props => (props.active ? '#f8fafc' : '#eef2ff')};
    border-color: #94a3b8;
  }

  &:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

export const NodeWrapper = styled.div<{ $layoutMode?: 'horizontal' | 'tree' }>`
  display: flex;
  flex-direction: column;
  align-items: ${props => (props.$layoutMode === 'tree' ? 'flex-start' : 'center')};
  position: relative;
`;

export const ChildrenColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 28px;
  position: relative;
  align-items: flex-start;
`;

export const TreeChildrenColumn = styled.div<{ $compactView?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${props => (props.$compactView ? '22px' : '16px')};
  margin-top: ${props => (props.$compactView ? '24px' : '20px')};
  margin-left: 120px;
  position: relative;
  padding-left: 20px;

  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #e6e6e6;
    z-index: 0;
  }
`;

export const TreeChildItem = styled.div`
  position: relative;

  &:before {
    content: '';
    position: absolute;
    left: -20px;
    top: 18px;
    width: 20px;
    height: 2px;
    background: #e6e6e6;
    z-index: 0;
  }
`;

export const TreeParentConnector = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: calc(50%);
  width: 2px;
  height: 24px;
  background: #e6e6e6;
  z-index: 0;
`;

export const NodeCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const NodeMenuButton = styled.button<{ $layoutMode?: 'horizontal' | 'tree'; $useWhiteIcons?: boolean }>`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: none;
  background: rgba(0,0,0,0.04);
  color: ${props => (props.$useWhiteIcons ? '#ffffff' : '#475569')};
  cursor: pointer;
  z-index: 5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
`;

export const NodeFilterButton = styled.button<{ $layoutMode?: 'horizontal' | 'tree'; $useWhiteIcons?: boolean }>`
  position: absolute;
  top: 8px;
  left: ${props => (props.$layoutMode === 'tree' ? 'auto' : '8px')};
  right: ${props => (props.$layoutMode === 'tree' ? '32px' : 'auto')};
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: none;
  background: rgba(0,0,0,0.04);
  color: ${props => (props.$useWhiteIcons ? '#ffffff' : '#475569')};
  cursor: pointer;
  z-index: 5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  &:hover {
    background: rgba(0,0,0,0.06);
  }
`;

export const CollapseButton = styled.button`
  position: absolute;
  top: calc(100% + 1px);
  left: 50%;
  transform: translateX(-50%);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid rgba(17,24,39,0.22);
  background: #ffffff;
  color: #111827;
  cursor: pointer;
  z-index: 6;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  transition: background 120ms ease, transform 120ms ease, box-shadow 120ms ease;
  &:hover {
    background: #f3f4f6;
    transform: translateX(-50%) translateY(-1px);
    box-shadow: 0 2px 6px rgba(15,23,42,0.12);
  }
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59,130,246,0.2);
  }
`;

export const CollapseIcon = styled.span<{ $hideOnDefault?: boolean }>`
  position: relative;
  left: 0;
  opacity: ${props => (props.$hideOnDefault ? 0 : 1)};
  transition: opacity 120ms ease;
  ${CollapseButton}:hover & {
    opacity: 1;
  }
`;

export const NodeMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px); /* slightly tighter below the node */
  right: 8px;
  background: white;
  border: 1px solid #e6e6e6;
  border-radius: 6px;
  box-shadow: 0 6px 18px rgba(17,24,39,0.08);
  z-index: 6;
  min-width: 120px;
  max-width: 180px;
  overflow: hidden;
`;

export const NodeMenuItem = styled.button`
  display: block;
  width: 100%;
  padding: 6px 10px; /* slightly tighter */
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  color: #111827;
  font-size: 14px;

  &:hover {
    background: #f3f4f6; /* darker hover for better contrast */
  }
`;

export const NodeBox = styled.div<{ $layoutMode?: 'horizontal' | 'tree' }>`
  position: relative;
  z-index: 3; /* ensure node boxes sit above connector lines */
  width: ${props => (props.$layoutMode === 'tree' ? '240px' : 'auto')};
  min-width: ${props => (props.$layoutMode === 'tree' ? '240px' : '200px')};
  max-width: ${props => (props.$layoutMode === 'tree' ? '240px' : '220px')};
  text-align: ${props => (props.$layoutMode === 'tree' ? 'left' : 'center')};
  border-radius: 8px;
  background: white;
  border: 1px solid rgba(17,24,39,0.12);
  box-shadow: 0 2px 8px rgba(17,24,39,0.20);
  overflow: hidden;
  padding: ${props => (props.$layoutMode === 'tree' ? '0px 0px 0px' : '0')};

  &.drop-target {
    border-color: rgba(34,197,94,0.85); /* green */
    box-shadow: 0 6px 18px rgba(34,197,94,0.08);
  }
`;

export const NodeInfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px 10px;
`;

export const TitleBar = styled.div<{ $color?: string | null; $layoutMode?: 'horizontal' | 'tree' }>`
  height: ${props => (props.$layoutMode === 'tree' ? '32px' : '36px')};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$color || '#f3f4f6'}; /* default light grey */
  color: ${props => (props.$color ? (isColorDark(props.$color) ? '#ffffff' : '#0f172a') : '#0f172a')};
  font-weight: 700;
  border-radius: 6px 6px 0 0;
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`;

export const TitleText = styled.div<{ $layoutMode?: 'horizontal' | 'tree' }>`
  padding: 4px 10px;
  font-size: 13px;
  line-height: 1.1;
  white-space: nowrap;
  overflow: ${props => (props.$layoutMode === 'tree' ? 'visible' : 'hidden')};
  text-overflow: ${props => (props.$layoutMode === 'tree' ? 'clip' : 'ellipsis')};
  width: 100%;
`;

export const DragHandle = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: rgba(0,0,0,0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #374151;
  font-size: 12px;
  cursor: grab;
  z-index: 5;
`;

export const NodeMeta = styled.div`
  color: #6b7280;
  font-size: 13px;
`;

export const MemberCount = styled(NodeMeta)<{ $layoutMode?: 'horizontal' | 'tree' }>`
  margin-top: ${props => (props.$layoutMode === 'tree' ? '0' : '6px')};
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
  }
`;

export const EmptyMessage = styled.div`
  color: #6b7280;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  padding: 30px 0;
`;

export const LeaderText = styled.span`
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ChildrenRow = styled.div`
  display: flex;
  gap: 28px;
  margin-top: 28px;
  position: relative;
  align-items: flex-start;
`;

export const HorizontalLine = styled.div`
  position: absolute;
  top: -18px;
  left: 0;
  right: 0;
  height: 2px;
  background: #e6e6e6;
  z-index: 1; /* sit behind node boxes */
`;

export const VerticalConnector = styled.div`
  position: absolute;
  top: 46px;
  left: calc(50% - 1px);
  width: 2px;
  height: 16px;
  background: #e6e6e6;
  z-index: 1; /* sit behind node boxes */
`;

export const IndependentSection = styled.div`
  margin-top: 14px;
  border-top: 1px dashed #e6e6e6;
  padding-top: 18px;
  display: flex;
  gap: 28px;
  flex-wrap: wrap;
  align-items: flex-start;
`;

export const NodeButton = styled.div`
  appearance: none;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  text-align: inherit;
  outline: none;
  &:focus {
    box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
    border-radius: 6px;
  }
`;
